import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { errorMessage } from "../api/client.ts";
import type { BrandAccess, Permission } from "../api/types.ts";
import { IconCheck } from "../components/Icons.tsx";
import { useToast } from "../components/Toast.tsx";
import { Spinner } from "../components/ui.tsx";
import { fmt, useT } from "../i18n/index.tsx";
import { useMe, useSession } from "../lib/session.tsx";

/** Pages and the permissions that open them, to decide whether to stay on the page after a switch. */
const PAGE_PERMS: [string, Permission[]][] = [
  ["/deposits", ["deposits:view"]], ["/payouts", ["payouts:view"]], ["/statement", ["statement:view"]], ["/vault", ["vault:view"]],
  ["/developers", ["api_keys:view", "webhook:view"]], ["/team", ["team:view"]], ["/activity", ["activity:view"]],
  ["/settings/security", []], ["/settings", ["settings:view"]],
];

/**
 * Topbar brand (merchant) name. With access to more than one brand it becomes a switcher: grouped by organization
 * when there are several, each brand with the person's role there and a "Paused" badge when payouts are paused.
 */
export function BrandSwitcher() {
  const t = useT();
  const me = useMe();
  const { switchMerchant } = useSession();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const loc = useLocation();
  const toast = useToast();
  const many = me.merchants.length > 1;

  const groups = me.merchants.reduce<{ org: string; name: string; items: BrandAccess[] }[]>((acc, b) => {
    const g = acc.find((x) => x.org === b.organization_id);
    if (g) g.items.push(b);
    else acc.push({ org: b.organization_id, name: b.organization_name, items: [b] });
    return acc;
  }, []);

  useEffect(() => {
    if (!open) return;
    ref.current?.querySelector<HTMLElement>('[aria-checked="true"]')?.focus();
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        btn.current?.focus();
      }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        const items = [...(ref.current?.querySelectorAll<HTMLElement>(".brand-item") ?? [])];
        const i = items.indexOf(document.activeElement as HTMLElement);
        items[(i + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length]?.focus();
        e.preventDefault();
      }
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = async (b: BrandAccess) => {
    if (b.id === me.merchant.id) return setOpen(false);
    setBusy(b.id);
    try {
      const next = await switchMerchant(b.id);
      setOpen(false);
      // Stay on the same page when the new role can open it, otherwise go to the overview. Filters are dropped.
      const page = PAGE_PERMS.find(([p]) => loc.pathname.startsWith(p));
      const ok = !page || page[1].length === 0 || page[1].some((p) => next.permissions.includes(p));
      navigate(ok ? loc.pathname : "/", { replace: true });
      toast(fmt(t.shell.switchedTo, { name: next.merchant.name }));
    } catch (e) {
      toast(errorMessage(e), "error");
    } finally {
      setBusy(null);
    }
  };

  const label = (
    <>
      <span className="topbar-label">{me.organization.name}</span>
      <strong>{me.merchant.name}</strong>
    </>
  );

  if (!many) return <div className="topbar-merchant">{label}</div>;

  return (
    <div className="brand-switch" ref={ref}>
      <button ref={btn} type="button" className="brand-btn" onClick={() => setOpen(!open)} aria-haspopup="menu" aria-expanded={open}
        aria-label={fmt(t.shell.brandLabel, { brand: me.merchant.name, org: me.organization.name })}>
        <span className="topbar-merchant">{label}</span>
        <svg className="brand-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="brand-panel" role="menu" aria-label={t.shell.switchBrand}>
          {groups.map((g) => (
            <div key={g.org} role="group" aria-label={g.name} className="brand-group">
              {groups.length > 1 && <div className="brand-group-name">{g.name}</div>}
              {g.items.map((b) => {
                const active = b.id === me.merchant.id;
                return (
                  <button key={b.id} type="button" role="menuitemradio" aria-checked={active} className={`brand-item ${active ? "active" : ""}`}
                    onClick={() => void pick(b)} disabled={!!busy}>
                    <span className="brand-item-text">
                      <strong>{b.name}</strong>
                      <span>{(t.common.roles as Record<string, string>)[b.role.toLowerCase()] ?? b.role}</span>
                    </span>
                    {b.in_review && <span className="review-tag">{t.shell.inReviewTag}</span>}
                    {b.payouts_paused && <span className="paused-tag">{t.common.statuses.paused}</span>}
                    <span className="brand-item-end" aria-hidden="true">{busy === b.id ? <Spinner /> : active ? <IconCheck size={16} /> : null}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
